import { cn } from "@cleanbudget/ui/lib/utils";
import { IconHelpCircle } from "@tabler/icons-react";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { layerHostOf, useHelp } from "./help-context";
import { type HelpTopicId, helpTopic } from "./topics";

/**
 * The (?) ("helpknop"; spec admin-help, "De (?) en de controle") beside a page title, a section or a
 * field: opens its explanation in the help panel, a second click closes it, and while its explanation
 * is shown it is filled in. "Uitleg over Staffelkorting" to a screen reader. In a sheet or dialog it
 * opens the help as a layer over it. Outside the admin's frame it shows nothing.
 */
export function HelpButton({ topic, className }: { topic: HelpTopicId; className?: string }) {
	const { t } = useTranslation();
	const help = useHelp();
	if (!help) return null;
	const active = help.shown === topic;
	return (
		<button
			type="button"
			aria-label={t("helpPanel.helpFor", { title: helpTopic(topic).title })}
			aria-expanded={active}
			data-help-topic={topic}
			onClick={(event) => {
				const button = event.currentTarget;
				help.toggle(topic, button, layerHostOf(button));
			}}
			className={cn(
				"inline-flex size-6 shrink-0 items-center justify-center rounded-full align-middle text-muted-foreground transition-colors outline-none hover:bg-muted hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50",
				active && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
				className,
			)}
		>
			<IconHelpCircle className="size-[18px]" aria-hidden />
		</button>
	);
}

/**
 * "Help" at the bottom of the menu: the overview, with search and every explanation. In the phone's
 * menu, which closes as the help opens, `returnFocusTo` is what gets the focus when the help closes
 * (the menu button), since this button is gone by then.
 */
export function HelpMenuButton({
	onClick,
	returnFocusTo,
}: {
	onClick?: () => void;
	returnFocusTo?: RefObject<HTMLElement | null>;
}) {
	const { t } = useTranslation();
	const help = useHelp();
	if (!help) return null;
	const active = help.shown === null;
	return (
		<button
			type="button"
			aria-expanded={active}
			onClick={(event) => {
				onClick?.();
				// Never a layer: in the phone's menu sheet, which closes now.
				help.toggle(null, returnFocusTo?.current ?? event.currentTarget, null);
			}}
			className={cn(
				"flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm whitespace-nowrap transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
				active && "bg-sidebar-accent font-medium text-sidebar-accent-foreground",
			)}
		>
			<IconHelpCircle className="size-4 shrink-0" aria-hidden />
			{t("helpPanel.help")}
		</button>
	);
}
